// Centralized data service for CFB scores
class DataService {
  constructor() {
    this.baseEndpoint = this.getEndpoint();
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Determine which endpoint to use based on environment
  getEndpoint() {
    // Check if we're in production by looking at hostname and protocol
    const isProduction =
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1";

    // In production (when served by Roda), use relative URLs
    // In development, use the Roda backend directly
    if (isProduction) {
      return "/api/cfb/data";
    } else {
      // For local development, use the Roda backend
      return "http://localhost:9494/api/cfb/data";
    }
  }

  // Generate cache key for data requests
  getCacheKey(date, comprehensive = false) {
    if (comprehensive) {
      return "cfb-data-comprehensive";
    }
    return `cfb-data-${date || "latest"}`;
  }

  // Check if cached data is still valid
  isCacheValid(cachedData) {
    if (!cachedData) return false;

    try {
      const parsed = JSON.parse(cachedData);
      const cacheAge = Date.now() - new Date(parsed.lastUpdated).getTime();
      return cacheAge < this.cacheTimeout;
    } catch {
      return false;
    }
  }

  // Load game data
  async loadGameData(date = null, forceRefresh = false) {
    const comprehensive = !date;
    const cacheKey = this.getCacheKey(date, comprehensive);

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData && this.isCacheValid(cachedData)) {
        const parsed = JSON.parse(cachedData);
        return parsed.games || [];
      }
    }

    try {
      // Always use the backend API
      const data = await this.fetchRemoteData(
        date,
        comprehensive,
        forceRefresh
      );

      // Cache the data
      this.cache.set(cacheKey, JSON.stringify(data));

      return data.games || [];
    } catch (error) {
      console.error("Error loading game data:", error);
      return [];
    }
  }

  // Fetch data from remote endpoint
  async fetchRemoteData(date, comprehensive = false, forceRefresh = false) {
    if (!this.baseEndpoint) {
      throw new Error("No API endpoint configured for production");
    }

    let url;

    if (date) {
      // Use the games endpoint for specific date
      const baseUrl = this.baseEndpoint.endsWith("/data")
        ? this.baseEndpoint.replace("/data", `/games/${date}`)
        : `${this.baseEndpoint}/games/${date}`;
      url = new URL(baseUrl, window.location.origin);
    } else {
      // Use the data endpoint with comprehensive parameter
      url = new URL(this.baseEndpoint, window.location.origin);
      url.searchParams.append("comprehensive", comprehensive);
    }

    // Add force refresh parameter if needed (only for manual refreshes)
    if (forceRefresh) {
      url.searchParams.append("force", "true");
    }

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // Get last update timestamp
  async getLastUpdateTime() {
    try {
      const cacheKey = "cfb-data-comprehensive";
      const cachedData = this.cache.get(cacheKey);

      if (cachedData && this.isCacheValid(cachedData)) {
        const parsed = JSON.parse(cachedData);
        return parsed.lastUpdated || null;
      }

      const data = await this.fetchRemoteData(false, true);
      return data.lastUpdated || null;
    } catch (error) {
      console.error("Error loading last update time:", error);
      return null;
    }
  }

  // Force refresh data
  async refreshGameData(date = null) {
    return await this.loadGameData(date, true);
  }

  // Load teams data
  async loadTeamsData(forceRefresh = false) {
    const cacheKey = "teams-data";

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData && this.isCacheValid(cachedData)) {
        const parsed = JSON.parse(cachedData);
        return parsed.teams || [];
      }
    }

    try {
      const teamsEndpoint = this.baseEndpoint.endsWith("/data")
        ? this.baseEndpoint.replace("/data", "/teams")
        : `${this.baseEndpoint}/teams`;

      let url = new URL(teamsEndpoint, window.location.origin);
      if (forceRefresh) {
        url.searchParams.append("force", "true");
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the data
      this.cache.set(cacheKey, JSON.stringify(data));

      return data.teams || [];
    } catch (error) {
      console.error("Error loading teams data:", error);
      return [];
    }
  }

  // Load rankings data
  async loadRankingsData(forceRefresh = false) {
    const cacheKey = "rankings-data";

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = this.cache.get(cacheKey);
      if (cachedData && this.isCacheValid(cachedData)) {
        const parsed = JSON.parse(cachedData);
        return parsed || null;
      }
    }

    try {
      const rankingsEndpoint = this.baseEndpoint.endsWith("/data")
        ? this.baseEndpoint.replace("/data", "/rankings")
        : `${this.baseEndpoint}/rankings`;

      let url = new URL(rankingsEndpoint, window.location.origin);
      if (forceRefresh) {
        url.searchParams.append("force", "true");
      }

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache the data
      this.cache.set(cacheKey, JSON.stringify(data));

      return data;
    } catch (error) {
      console.error("Error loading rankings data:", error);
      return null;
    }
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
  }

  // Set custom endpoint (for testing or configuration)
  setEndpoint(endpoint) {
    this.baseEndpoint = endpoint;
    this.clearCache(); // Clear cache when endpoint changes
  }
}

// Create singleton instance
const dataService = new DataService();

export default dataService;
export { DataService };
